import { useAction, useAtom } from '@reatom/npm-react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { AppDataTable } from '@root/features/app-data-table/app-data-table'
import {
  DataCounterButton,
  TooltipCardParentGuardProvider,
} from '@root/shared/ui-kit'

import {
  premiumFeedTableModel,
  unseenPremiumActivitiesCountAtom,
} from '../../atoms/reatom-premium-feed-table'

import type { PremiumFeedTableColumnId } from '../../atoms/reatom-premium-feed-table'
import type { PairActivity } from '../../model'
import type { ClassNameProps } from '@root/shared/react'
import type { TableVirtuosoHandle } from 'react-virtuoso'

type PremiumFeedTableProps = {} & ClassNameProps

export const PremiumFeedTable: React.FC<PremiumFeedTableProps> = ({
  className,
}) => {
  const model = premiumFeedTableModel
  const [paused, setPaused] = useAtom(model.paused)
  const [scrolled, setScrolled] = useState(false)
  const [counter] = useAtom(unseenPremiumActivitiesCountAtom)
  const flushBuffer = useAction(model.flushBuffer)
  const virtuosoRef = useRef<TableVirtuosoHandle>(null)
  const nestedOverlayOpenRef = useRef(false)
  const { t } = useTranslation()

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const scrollTop = e.currentTarget.scrollTop
    const passedThreshold = scrollTop > 20
    setScrolled(passedThreshold)
    if (passedThreshold) {
      setPaused(passedThreshold)
    }
  }

  const handleMouseLeave: React.MouseEventHandler<HTMLDivElement> = () => {
    // A pair popover opened from a currency cell is portaled outside the feed,
    // so moving the pointer onto it fires `mouseleave` here. Stay paused while
    // it's open — resuming would reflow the rows and close the popover under
    // the cursor.
    if (scrolled || nestedOverlayOpenRef.current) {
      return
    }
    setPaused(false)
  }

  const handleNewDataClick = () => {
    flushBuffer()
    virtuosoRef.current?.scrollToIndex({
      index: 0,
      behavior: 'smooth',
    })
  }

  const floatingActions =
    paused && counter > 0 ? (
      <DataCounterButton
        counter={counter}
        text={t('wallet-tracker.new-alerts')}
        onClick={handleNewDataClick}
      />
    ) : undefined

  return (
    <Root onMouseOver={() => setPaused(true)} onMouseLeave={handleMouseLeave}>
      <TooltipCardParentGuardProvider
        nestedOverlayOpenRef={nestedOverlayOpenRef}
      >
        <Table
          virtuosoRef={virtuosoRef}
          floatingActions={floatingActions}
          className={className}
          rowHeight={40}
          model={model}
          onScroll={handleScroll}
        />
      </TooltipCardParentGuardProvider>
    </Root>
  )
}

const Root = styled.div`
  position: relative;
`

const Table = styled(
  AppDataTable<PairActivity.PremiumActivity, PremiumFeedTableColumnId>,
)`
  --data-table-row-even-bg: var(--background-1);
  --data-table-row-odd-bg: var(--backgroundA-1);
  --data-table-row-border-color: var(--background-2);
`
